import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LazyYouTubeEmbed from './LazyYouTubeEmbed';
import { reviewsData } from '../data/reviewsData';

const { FiArrowLeft, FiStar, FiExternalLink, FiShare2, FiBookmark } = FiIcons;

// Simple content renderer to replace ReactMarkdown
const ContentRenderer = ({ content }) => {
  // Split content by double newlines to create paragraphs
  const paragraphs = content.split('\n\n').filter(p => p.trim());
  
  return (
    <div className="content-renderer">
      {paragraphs.map((paragraph, index) => {
        // Handle headers (lines starting with #)
        if (paragraph.startsWith('### ')) {
          return (
            <h3 key={index} style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#ffffff',
              marginTop: '32px',
              marginBottom: '16px'
            }}>
              {paragraph.replace('### ', '')}
            </h3>
          );
        }
        
        if (paragraph.startsWith('## ')) {
          return (
            <h2 key={index} style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#ffffff',
              marginTop: '40px',
              marginBottom: '20px'
            }}>
              {paragraph.replace('## ', '')}
            </h2>
          );
        }
        
        // Handle bold text (**text**)
        const formatText = (text) => {
          return text.split(/(\*\*.*?\*\*)/).map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return (
                <strong key={i} style={{ color: '#00ff88', fontWeight: '600' }}>
                  {part.slice(2, -2)}
                </strong>
              );
            }
            return part;
          });
        };
        
        // Regular paragraph
        return (
          <p key={index} style={{
            fontSize: '16px',
            color: '#e0e0e0',
            lineHeight: '1.7',
            marginBottom: '20px'
          }}>
            {formatText(paragraph)}
          </p>
        );
      })}
    </div>
  );
};

const ReviewPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const review = reviewsData.find(r => r.slug === slug);

  useEffect(() => {
    if (!review) {
      navigate('/reviews');
      return;
    }
    
    // Update page title and meta description for SEO
    document.title = `${review.title} | Extraterrestrial Tools Reviews`;
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', review.description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = review.description;
      document.head.appendChild(meta);
    }

    // Scroll to top
    window.scrollTo(0, 0);
  }, [review, navigate]);

  if (!review) {
    return null;
  }

  // Extract tool name from the title for dynamic CTA text
  const getToolName = (title) => {
    // Handle different title formats
    if (title.includes(':')) {
      const beforeColon = title.split(':')[0].trim();
      // Remove common words like "Review", "Deep Dive", etc.
      return beforeColon
        .replace(/\s+(Review|Deep Dive|Real Talk About|Analysis)$/i, '')
        .trim();
    }
    
    // For titles without colons, try to extract the main tool name
    const words = title.split(' ');
    if (words.length > 0) {
      // Return the first word that's likely a tool name (not common words)
      const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'review', 'analysis'];
      const toolName = words.find(word => 
        !commonWords.includes(word.toLowerCase()) && 
        word.length > 2
      );
      return toolName || words[0];
    }
    
    return 'This Tool';
  };

  const toolName = getToolName(review.title);

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - Math.ceil(rating);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {[...Array(fullStars)].map((_, index) => (
          <SafeIcon
            key={`full-${index}`}
            icon={FiStar}
            style={{ width: '20px', height: '20px', color: '#00ff88' }}
          />
        ))}
        
        {hasHalfStar && (
          <div style={{ position: 'relative' }}>
            <SafeIcon
              icon={FiStar}
              style={{ width: '20px', height: '20px', color: '#333' }}
            />
            <div style={{ 
              position: 'absolute', 
              inset: '0', 
              overflow: 'hidden', 
              width: '50%' 
            }}>
              <SafeIcon
                icon={FiStar}
                style={{ width: '20px', height: '20px', color: '#00ff88' }}
              />
            </div>
          </div>
        )}
        
        {[...Array(emptyStars)].map((_, index) => (
          <SafeIcon
            key={`empty-${index}`}
            icon={FiStar}
            style={{ width: '20px', height: '20px', color: '#333' }}
          />
        ))}
      </div>
    );
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = review.title;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: review.description,
          url: url,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(url).then(() => {
        // You could show a toast notification here
        console.log('Link copied to clipboard');
      });
    }
  };

  return (
    <div className="app">
      <div className="stars"></div>
      
      <main className="main">
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: '32px' }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginBottom: '16px'
            }}>
              <Link
                to="/reviews"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '50px',
                  padding: '12px 20px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  textDecoration: 'none',
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(0, 255, 136, 0.1)';
                  e.target.style.borderColor = '#00ff88';
                  e.target.style.boxShadow = '0 0 20px rgba(0, 255, 136, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <SafeIcon icon={FiArrowLeft} style={{ fontSize: '18px' }} />
                <span>Back to Reviews</span>
              </Link>

              <Link
                to="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '50px',
                  padding: '12px 20px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  textDecoration: 'none',
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <span>← Back to Tools</span>
              </Link>
            </div>
          </motion.div>

          {/* Review Header */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              padding: '40px',
              backdropFilter: 'blur(20px)',
              marginBottom: '32px'
            }}
          >
            {/* Title and Rating */}
            <header style={{ marginBottom: '32px' }}>
              <h1 style={{
                fontSize: 'clamp(24px, 4vw, 42px)',
                fontWeight: '800',
                lineHeight: '1.2',
                marginBottom: '16px',
                color: '#ffffff'
              }}>
                {review.title}
              </h1>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
                flexWrap: 'wrap',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {renderStars(review.rating)}
                  <span style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#00ff88'
                  }}>
                    {review.rating.toFixed(1)}
                  </span>
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <motion.button
                    onClick={handleShare}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      padding: '8px 16px',
                      color: '#ffffff',
                      fontSize: '14px',
                      cursor: 'pointer',
                      backdropFilter: 'blur(10px)',
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiShare2} style={{ fontSize: '16px' }} />
                    Share
                  </motion.button>
                </div>
              </div>

              <p style={{
                fontSize: '18px',
                color: '#b0b0b0',
                lineHeight: '1.6',
                marginBottom: '24px'
              }}>
                {review.description}
              </p>

              {/* CTA Button */}
              {review.affiliateLink && (
                <motion.a
                  href={review.affiliateLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '18px',
                    padding: '16px 32px',
                    textDecoration: 'none',
                    borderRadius: '16px'
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Try {toolName}</span>
                  <SafeIcon icon={FiExternalLink} style={{ fontSize: '20px' }} />
                </motion.a>
              )}
            </header>

            {/* Video Section */}
            {review.videoId && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{ marginBottom: '48px' }}
              >
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#ffffff',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '28px' }}>📺</span>
                  Video Review
                </h2>
                <LazyYouTubeEmbed 
                  videoId={review.videoId} 
                  title={`${review.title} - Video Review`}
                />
              </motion.section>
            )}

            {/* Pros and Cons */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '32px',
                marginBottom: '48px'
              }}
            >
              <div>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#00ff88',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '28px' }}>✓</span>
                  Pros
                </h2>
                <ul style={{ listStyle: 'none', padding: '0' }}>
                  {review.pros.map((pro, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: '16px',
                        color: '#e0e0e0',
                        lineHeight: '1.6',
                        fontSize: '16px'
                      }}
                    >
                      <span style={{
                        color: '#00ff88',
                        marginRight: '12px',
                        fontSize: '18px',
                        fontWeight: '600'
                      }}>•</span>
                      <span>{pro}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#ff00ff',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '28px' }}>✗</span>
                  Cons
                </h2>
                <ul style={{ listStyle: 'none', padding: '0' }}>
                  {review.cons.map((con, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: '16px',
                        color: '#e0e0e0',
                        lineHeight: '1.6',
                        fontSize: '16px'
                      }}
                    >
                      <span style={{
                        color: '#ff00ff',
                        marginRight: '12px',
                        fontSize: '18px',
                        fontWeight: '600'
                      }}>•</span>
                      <span>{con}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.section>

            {/* Full Review Content */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '24px'
              }}>
                Full Review
              </h2>
              
              <ContentRenderer content={review.content} />
            </motion.section>

            {/* Bottom CTA */}
            {review.affiliateLink && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                style={{
                  marginTop: '48px',
                  padding: '32px',
                  background: 'rgba(0, 255, 136, 0.1)',
                  border: '1px solid rgba(0, 255, 136, 0.3)',
                  borderRadius: '20px',
                  textAlign: 'center'
                }}
              >
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#00ff88',
                  marginBottom: '16px'
                }}>
                  Ready to try {toolName}?
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: '#b0b0b0',
                  marginBottom: '24px',
                  maxWidth: '600px',
                  margin: '0 auto 24px auto'
                }}>
                  Based on our comprehensive testing and analysis, this tool offers excellent value for its intended use cases.
                </p>
                <motion.a
                  href={review.affiliateLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '18px',
                    padding: '16px 32px',
                    textDecoration: 'none',
                    borderRadius: '16px'
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Try {toolName} Now</span>
                  <SafeIcon icon={FiExternalLink} style={{ fontSize: '20px' }} />
                </motion.a>
              </motion.div>
            )}
          </motion.article>

          {/* Related Reviews */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            style={{ marginBottom: '40px' }}
          >
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              More Reviews
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {reviewsData
                .filter(r => r.slug !== slug)
                .slice(0, 3)
                .map((relatedReview, index) => (
                  <motion.div
                    key={relatedReview.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.9 + index * 0.1 }}
                  >
                    <Link
                      to={`/reviews/${relatedReview.slug}`}
                      style={{
                        display: 'block',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        padding: '20px',
                        textDecoration: 'none',
                        color: 'inherit',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-4px)';
                        e.target.style.borderColor = 'rgba(0, 255, 136, 0.3)';
                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                      }}
                    >
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#ffffff',
                        marginBottom: '8px',
                        lineHeight: '1.3'
                      }}>
                        {relatedReview.title}
                      </h3>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px'
                      }}>
                        {renderStars(relatedReview.rating)}
                        <span style={{
                          fontSize: '14px',
                          color: '#00ff88',
                          fontWeight: '600'
                        }}>
                          {relatedReview.rating.toFixed(1)}
                        </span>
                      </div>
                      <p style={{
                        fontSize: '14px',
                        color: '#b0b0b0',
                        lineHeight: '1.4',
                        margin: '0'
                      }}>
                        {relatedReview.description}
                      </p>
                    </Link>
                  </motion.div>
                ))}
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
};

export default ReviewPage;